import { useEffect, useRef } from "react";

import { AUTH_TOKEN_KEY, getToken } from "@/utils/auth";
import { apiRequest } from "@/lib/api";
import type { User } from "@/lib/types";

// Matches the settle window in lib/api.ts.
const REMOVAL_RECHECK_MS = 300;

/**
 * Calls back when another tab changes or clears the stored auth token.
 * `storage` events only fire in other tabs, never the one that wrote.
 *
 * Token rotation can look like "removed then set" to other tabs (e.g. a tab
 * whose request used the old token clears it just before the rotating tab
 * writes the new one). So a removal is never trusted at face value: we re-read
 * storage after a short delay and report logout (null) only if there is still
 * no token. If one has appeared, it is reported as a replacement (refresh).
 */
export function useAuthTokenSync(
  onChange: (newToken: string | null) => void,
  enabled = true
) {
  const ref = useRef(onChange);
  useEffect(() => {
    ref.current = onChange;
  });

  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const handler = (e: StorageEvent) => {
      // key === null means localStorage.clear()
      if (e.key !== null && e.key !== AUTH_TOKEN_KEY) return;
      clearTimeout(timer);
      if (e.key !== null && e.newValue) {
        ref.current(e.newValue);
        return;
      }
      timer = setTimeout(() => ref.current(getToken()), REMOVAL_RECHECK_MS);
    };
    window.addEventListener("storage", handler);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("storage", handler);
    };
  }, [enabled]);
}

/**
 * Keeps a rendered session in step with other tabs. Logout -> onLogout.
 * Token replaced -> re-fetch /me/: same user just refreshes state, a different
 * user (account switched in another tab) hard-reloads so nothing rendered or
 * cached belongs to the previous user.
 */
export function useAuthSessionSync({
  user,
  onUser,
  onLogout,
  enabled = true,
}: {
  user: User | null;
  onUser: (user: User) => void;
  onLogout: () => void;
  enabled?: boolean;
}) {
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  });

  useAuthTokenSync((newToken) => {
    if (!newToken) {
      onLogout();
      return;
    }
    apiRequest<User>("get", "/me/")
      .then((data) => {
        const rendered = userRef.current;
        if (rendered && data.id !== rendered.id) {
          window.location.reload();
          return;
        }
        onUser(data);
      })
      .catch(() => {});
  }, enabled);
}
